/**
 * Tenant role resolution for the browser.
 *
 * This is the CLIENT half of the fix that landed server-side in
 * `src/shared/utils/firebase-auth.ts` (`resolveUserTenant`). The backend can
 * grade a member correctly and the page still be wrong: the invite button,
 * Pending Invites and the Actions column on `/settings/team` are all gated on
 * a role the client derives for itself. A backend-only fix leaves a claims-less
 * tenant admin staring at a read-only page while every backend test passes.
 *
 * Kept as pure functions in their own module so the rules are testable without
 * a Firebase or React harness — the logic that decides what a user may do
 * should not be reachable only through a manual click-through.
 *
 * Rules are mirrored from the backend deliberately. If you change one side,
 * change the other.
 */

/** The tenant role floor. Nobody resolves to less, and nobody is GUESSED above it. */
export const DEFAULT_TENANT_ROLE = 'viewer';

const TENANT_ROLE_VALUES: readonly string[] = ['admin', 'manager', 'support', 'viewer'];

export type DocData = Record<string, unknown> | undefined;

/** Accept a stored/claimed tenant role only if it is one of the four real values. */
export function asTenantRole(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  return TENANT_ROLE_VALUES.includes(normalized) ? normalized : undefined;
}

/**
 * Tenant role from the token claims.
 *
 * `tenantRole` is the only claim that carries a tenant role faithfully. The
 * legacy `role` claim does NOT: `tenantToLegacyRole` collapses
 * manager -> tenant_admin and viewer -> tenant_user, so reading tenant_admin
 * back as 'admin' PROMOTES EVERY MANAGER TO ADMIN. A legacy-only claim is
 * therefore treated as no role information at all — resolution continues to the
 * stored role, and failing that to the floor.
 *
 * super_admin / kombatix_qa are different in kind and stay inferable: no tenant
 * role collapses into them, so reading them as full tenant capability is exact
 * rather than a guess. (`tokenRole` carries the un-flattened value for the
 * platform-admin checks in `tenant-context`.)
 */
export function tenantRoleFromClaims(claims: Record<string, unknown>): string | undefined {
  const explicit = asTenantRole(claims.tenantRole);
  if (explicit) return explicit;
  if (claims.role === 'super_admin' || claims.role === 'kombatix_qa') return 'admin';
  return undefined;
}

/**
 * Whether a stored `status` field refuses access.
 *
 * ABSENT MUST READ AS ACTIVE. No `user_mappings` document written before
 * 2026-08-09 carries a `status` field — they hold {companyId, uid, role} — so
 * `status !== 'active' -> refuse` would lock out every existing user (there are
 * three, all real) the moment it shipped. Only an EXPLICIT non-active value
 * refuses.
 */
export function statusRefuses(status: unknown): boolean {
  if (status === undefined || status === null || status === '') return false;
  return String(status).trim().toLowerCase() !== 'active';
}

/**
 * Resolve the effective tenant role:
 *
 *   claim `tenantRole` -> users/{uid}.tenantRole -> mapping.tenantRole -> viewer
 *
 * Returns `null` when an explicit non-active `status` on EITHER document
 * refuses access outright.
 *
 * The mapping is consulted even when `users/{uid}` exists. That is the whole
 * point: nothing writes `tenantRole` onto a `users/{uid}` document, so a
 * short-circuit there can never see the role that was backfilled for the
 * member. Custom claims are best-effort (`createMember` logs and continues when
 * setting them fails), which is exactly why `user_mappings` carries the real
 * role — a tenant admin whose claims never landed signed in fine and graded
 * `viewer`, so their own invite button never rendered.
 *
 * A role is NEVER reconstructed from the legacy `role` field on either
 * document; see `tenantRoleFromClaims` for why that promotes managers to
 * admins.
 */
export function resolveEffectiveTenantRole(
  claims: Record<string, unknown>,
  userDoc: DocData,
  mapping: DocData,
): string | null {
  if (statusRefuses(userDoc?.status)) return null;
  if (statusRefuses(mapping?.status)) return null;

  return tenantRoleFromClaims(claims)
    ?? asTenantRole(userDoc?.tenantRole)
    ?? asTenantRole(mapping?.tenantRole)
    ?? DEFAULT_TENANT_ROLE;
}

export interface TenantAccess {
  /**
   * Undefined when nothing names a tenant. That is NOT a refusal: a Kombatix
   * platform admin is provisioned `{ tenantId: null, role: 'super_admin' }` by
   * `scripts/set-kombatix-claims.ts` and has neither document, yet must keep
   * `tokenRole` or the tenant selector — their only way to pick a tenant —
   * stops rendering.
   */
  companyId?: string;
  /** Legacy Firestore/claim role. Display and admin-portal link only — never a tenant role. */
  role?: string;
  tenantRole: string;
  /** Raw platform claim (`super_admin` / `kombatix_qa` / `tenant_*`), un-flattened. */
  tokenRole?: string;
}

export interface TenantAccessInput {
  uid: string;
  claims: Record<string, unknown>;
  /** `users/{uid}` document data, if it exists. */
  userDoc?: Record<string, unknown>;
  /** `user_mappings/{email}` document data, if it exists. */
  mapping?: Record<string, unknown>;
}

/**
 * Is this `user_mappings` document actually about the signed-in user?
 *
 * Mirrors the backend gate. The mapping is keyed by EMAIL, so it is exactly as
 * trustworthy as the email — and Firebase does not verify one on email/password
 * signup. Accept EITHER a uid recorded at invite-acceptance (self-authenticating:
 * the invited user's uid matches, someone who merely registered the address has
 * a different one) OR a verified email (covers mappings written before the uid
 * was recorded, and Google sign-ins, which always carry the claim).
 */
function isMappingTrusted(uid: string, claims: Record<string, unknown>, mapping: DocData): boolean {
  if (!mapping) return false;
  if (typeof mapping.uid === 'string' && mapping.uid === uid) return true;
  return claims.email_verified === true;
}

/**
 * Resolve tenant identity and role for a signed-in user — the client mirror of
 * the backend's `resolveUserTenant`.
 *
 * Tenant identity (first hit wins): users/{uid} -> user_mappings/{email} -> claims.
 * Tenant ROLE is resolved separately, because the document that names the
 * tenant is not always the document that knows the role.
 *
 * Returns `null` ONLY when access is REFUSED — an explicit non-active `status`
 * on either document. A refusal hands back NOTHING, not even `tokenRole`:
 * `isPlatformAdmin` is derived from that claim and short-circuits every role
 * check, so returning it would give a disabled super_admin the whole app off a
 * stale token (disabling clears custom claims, but the old token still carries
 * them until it refreshes).
 *
 * Failing to NAME a tenant is not a refusal — see `TenantAccess.companyId`.
 */
export function resolveTenantAccess(input: TenantAccessInput): TenantAccess | null {
  const { uid, claims, userDoc, mapping } = input;

  // A disabled mapping refuses however it was reached, before the trust gate is
  // even consulted — honouring a revocation is fail-closed, and nothing outside
  // our own services can write one.
  if (statusRefuses(mapping?.status)) return null;

  const trustedMapping = isMappingTrusted(uid, claims, mapping) ? mapping : undefined;

  const tenantRole = resolveEffectiveTenantRole(claims, userDoc, trustedMapping);
  if (tenantRole === null) return null;

  const tokenRole = typeof claims.role === 'string' ? claims.role : undefined;

  if (typeof userDoc?.companyId === 'string' && userDoc.companyId) {
    return {
      companyId: userDoc.companyId,
      role: typeof userDoc.role === 'string' ? userDoc.role : undefined,
      tenantRole,
      tokenRole,
    };
  }

  if (typeof trustedMapping?.companyId === 'string' && trustedMapping.companyId) {
    return {
      companyId: trustedMapping.companyId,
      role: typeof trustedMapping.role === 'string' ? trustedMapping.role : undefined,
      tenantRole,
      tokenRole,
    };
  }

  const claimed = claims.companyId ?? claims.tenantId;
  if (typeof claimed === 'string' && claimed) {
    return { companyId: claimed, role: tokenRole, tenantRole, tokenRole };
  }

  // Nothing names a tenant. Grant the verified identity anyway, with no
  // companyId — the backend will 403 any tenant-scoped call, and a Kombatix
  // platform admin legitimately lives here.
  return { tenantRole, tokenRole };
}
